import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { langueCourante, traduire } from '@/i18n/language'
import { estErreurChargementModule, repriseChargement, type EtatReprise } from '@/lib/repriseChargement'

type Props = {
  screenName: string
  children: ReactNode
  /** L'adresse courante : la changer referme l'échec, sinon l'écran suivant en hériterait. */
  cleReprise?: string
}
type State = { failed: boolean; modulePerdu: boolean; reprise: EtatReprise }

/**
 * Isole le rejet d'un chunk ou l'exception d'un écran de la coquille applicative.
 * Le détail technique est volontairement ignoré : il peut contenir une URL signée,
 * un chemin interne ou le corps d'une réponse.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, modulePerdu: false, reprise: 'relance' }

  static getDerivedStateFromError(erreur: unknown): Partial<State> {
    return { failed: true, modulePerdu: estErreurChargementModule(erreur), reprise: 'relance' }
  }

  componentDidCatch(erreur: Error, _info: ErrorInfo): void {
    // La télémétrie, si elle existe, est branchée hors de ce composant. Ne jamais
    // rendre ni sérialiser l'exception brute dans le navigateur.
    if (!estErreurChargementModule(erreur)) return
    const reprise = repriseChargement()
    if (reprise) reprise.planifier(this.suivreReprise)
    else this.setState({ reprise: 'epuise' })
  }

  componentDidUpdate(precedent: Props): void {
    if (this.state.failed && precedent.cleReprise !== this.props.cleReprise) this.refermer()
  }

  componentWillUnmount(): void {
    if (this.state.modulePerdu) repriseChargement()?.annuler(this.suivreReprise)
  }

  private suivreReprise = (etat: EtatReprise): void => this.setState({ reprise: etat })

  private refermer(): void {
    if (this.state.modulePerdu) repriseChargement()?.annuler(this.suivreReprise)
    this.setState({ failed: false, modulePerdu: false })
  }

  /** React.lazy garde le rejet d'un module perdu : seule une relance de la page le rejoue. */
  private reessayer = (): void => {
    if (!this.state.modulePerdu) { this.refermer(); return }
    const reprise = repriseChargement()
    if (reprise) reprise.recharger()
    else window.location.reload()
  }

  private indice(t: (cle: string, repli: string) => string): string {
    if (!this.state.modulePerdu) return t('echecEcran.indice', 'La navigation reste disponible. Réessayez ou revenez au tableau de bord.')
    if (this.state.reprise === 'hors-ligne') return t('echecEcran.indiceHorsLigne', 'Vous semblez hors ligne. L’écran se rechargera seul au retour du réseau.')
    if (this.state.reprise === 'epuise') return t('echecEcran.indiceEpuise', 'La connexion semble interrompue. Vérifiez-la, puis réessayez.')
    return t('echecEcran.indiceRelance', 'La connexion semble interrompue. Nouvelle tentative automatique…')
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children
    // Composant de classe : pas de hook ; la langue courante est lue au rendu de l'échec.
    const lang = langueCourante()
    const t = (cle: string, repli: string, v?: Record<string, string>): string => traduire(lang, cle, repli, v)
    const ecran = { ecran: this.props.screenName }
    return (
      <section className="state state--error" role="alert" aria-label={t('echecEcran.region', 'Échec de {{ecran}}', ecran)}>
        <p className="state__title">{t('echecEcran.titre', 'Impossible d’ouvrir {{ecran}}.', ecran)}</p>
        <p className="state__hint">{this.indice(t)}</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="button" onClick={this.reessayer}>
            {t('echecEcran.reessayer', 'Réessayer')}
          </button>
          <Link className="button" to="/">{t('acces.retourTableauDeBord', 'Retour au tableau de bord')}</Link>
        </div>
      </section>
    )
  }
}
